import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Loader2, Trash2, ChevronLeft, MapPin } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useOsMe, OsDenied, OS_WRITE_ROLES } from "./osShared";
import LeafletMap from "./LeafletMap";

export default function OsProperties() {
  const { me, denied } = useOsMe();
  const [data, setData] = useState(null);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ designation: "", lat: "59.33", lng: "18.06", areaSqM: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/os/properties?limit=100").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (denied) return <OsDenied />;
  const canWrite = me && OS_WRITE_ROLES.includes(me.role);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/os/properties", {
        designation: form.designation,
        geometry: { type: "Point", coordinates: [parseFloat(form.lng), parseFloat(form.lat)] },
        areaSqM: form.areaSqM ? parseFloat(form.areaSqM) : null,
      });
      toast.success("Fastighet skapad");
      setShow(false);
      setForm({ designation: "", lat: "59.33", lng: "18.06", areaSqM: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte skapa");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/os/properties/${p.id}`);
      toast.success("Fastighet borttagen");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kan inte tas bort");
    }
  };

  return (
    <div className="max-w-4xl mx-auto animate-fade-in-up" data-testid="os-properties-page">
      <Link to="/os" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-blue-700">
        <ChevronLeft className="w-4 h-4" /> Ärenden
      </Link>
      <div className="mt-3 flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>Bygglovs-OS · Fastigheter</h1>
        {canWrite && (
          <button onClick={() => setShow(true)} data-testid="os-new-property"
            className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-sm text-sm font-medium">
            <Plus className="w-4 h-4" /> Ny fastighet
          </button>
        )}
      </div>

      {!data ? (
        <div className="flex justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>
      ) : data.items.length === 0 ? (
        <div className="mt-6 bg-zinc-50 border border-zinc-200 rounded-sm p-10 text-center text-sm text-zinc-500" data-testid="os-properties-empty">Inga fastigheter.</div>
      ) : (
        <div className="mt-5 bg-white border border-zinc-200 rounded-sm divide-y divide-zinc-100" data-testid="os-properties-list">
          {data.items.map((p) => (
            <div key={p.id} className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-blue-700" />
                <div>
                  <div className="text-sm font-medium text-zinc-800">{p.designation}</div>
                  <div className="text-[11px] text-zinc-400">
                    {p.coordinateSystem} · {p.geometry?.coordinates?.join(", ")}{p.areaSqM ? ` · ${p.areaSqM} m²` : ""}
                  </div>
                </div>
              </div>
              {canWrite && (
                <button onClick={() => remove(p)} data-testid={`os-property-delete-${p.id}`}
                  className="p-1.5 text-zinc-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          ))}
        </div>
      )}

      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/40 backdrop-blur-sm" data-testid="os-property-modal">
          <form onSubmit={submit} className="w-full max-w-md bg-white border border-zinc-200 rounded-sm p-6 space-y-3">
            <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Ny fastighet</h3>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Fastighetsbeteckning *</label>
              <input required value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })}
                data-testid="os-property-designation" placeholder="Täby 3:12"
                className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm outline-none focus:border-blue-700" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Position (klicka i kartan)</label>
              <div className="mt-1">
                <LeafletMap
                  markers={[{ lat: parseFloat(form.lat) || 59.33, lng: parseFloat(form.lng) || 18.06, label: form.designation || "Ny fastighet" }]}
                  onMapClick={(ll) => setForm({ ...form, lat: ll.lat.toFixed(6), lng: ll.lng.toFixed(6) })}
                  height="h-[220px]"
                  testid="os-property-map"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Lat</label>
                <input value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} data-testid="os-property-lat"
                  className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm outline-none focus:border-blue-700" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Lng</label>
                <input value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} data-testid="os-property-lng"
                  className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm outline-none focus:border-blue-700" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Area m²</label>
                <input value={form.areaSqM} onChange={(e) => setForm({ ...form, areaSqM: e.target.value })} data-testid="os-property-area"
                  className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm outline-none focus:border-blue-700" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm">Avbryt</button>
              <button type="submit" disabled={busy} data-testid="os-property-submit"
                className="px-4 py-2 text-sm bg-blue-700 text-white rounded-sm disabled:opacity-50 inline-flex items-center gap-2">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Skapa
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
